import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-white p-12">
        <p className="font-display font-bold text-lg">Dr. Saurabh Agarwal Clinic</p>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Staff portal</h1>
          <p className="mt-4 text-white/70 max-w-sm">Review new appointment requests, confirm visits and keep the day's schedule on track.</p>
        </div>
        <p className="text-sm text-white/50">Consultant Physician & Diabetologist · Lucknow</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="login-back-link"><ArrowLeft className="h-4 w-4" /> Back to website</Link>
          <span className="mt-10 h-12 w-12 rounded-2xl bg-accent text-primary grid place-items-center"><Lock className="h-5 w-5" /></span>
          <h2 className="mt-5 text-2xl font-semibold text-[#1A252C]">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">For doctor and clinic staff only.</p>
          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" className="h-11" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" className="h-11" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-11 rounded-full" disabled={loading} data-testid="login-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
